import { call, put, select } from 'redux-saga/effects';
import {
  getPaySecureSessionId,
  hasCardBeenTokenized,
} from '@tcp/core/src/components/features/CnC/Checkout/container/CheckoutPaySecure.selector';
import CHECKOUT_CONSTANTS from '@tcp/core/src/components/features/CnC/Checkout/Checkout.constants';
import logger from '@tcp/core/src/utils/loggerInstance';
import { getSelectedCard } from '@tcp/core/src/components/features/CnC/Checkout/util/utility';
import { getCardsList } from '@tcp/core/src/components/features/account/Payment/container/Payment.selectors';
import selectors, { getIsPaySecureFailed } from './Checkout.selector';
import CONSTANTS from '../Checkout.constants';
import {
  fetchPaySecureSessionIdError,
  fetchPaySecureSessionIdSuccess,
  updateCardTokenization,
} from './Checkout.action';

import { getAPIConfig, parseBoolean } from '../../../../../utils';
import {
  tokenizeCard,
  fetchPaySecureSessionId,
} from '../../../../../services/abstractors/CnC/PaySecure';
import {
  addPaymentInstruction,
  addPIOfflineToSFCC,
  getPaymentInstruction,
} from '../../../../../services/abstractors/CnC/CheckoutSFCC';

export function* fetchPaySecureSessionIdSaga(skipExistingSessionId = false) {
  let response;
  const sessionId = yield select(getPaySecureSessionId);
  try {
    if (!skipExistingSessionId && sessionId) {
      response = {
        SessionId: sessionId,
      };
    } else {
      response = yield call(fetchPaySecureSessionId);
      yield put(fetchPaySecureSessionIdSuccess(response));
    }
  } catch (e) {
    yield put(fetchPaySecureSessionIdError({ error: e }));
    logger.error({
      error: e,
      extraData: {
        methodName: 'PaySecure - getPaySecureSessionId',
      },
    });
    throw e;
  }

  return response;
}

export function* getBillingAddress(formData, address) {
  let billingAddress;
  const { phoneNumber = '' } = formData;
  const isBillingAddressSameAsShipping = formData?.address?.sameAsShipping;
  if (isBillingAddressSameAsShipping) {
    const shippingDetails = yield select(selectors.getShippingDestinationValues);
    billingAddress = shippingDetails?.address;
    billingAddress.emailAddress = shippingDetails?.emailAddress ? shippingDetails.emailAddress : '';
    billingAddress.phoneNumber = shippingDetails?.phoneNumber;
    billingAddress.sameAsShippingAddress = isBillingAddressSameAsShipping;
  } else {
    billingAddress = address;
    billingAddress.phoneNumber = phoneNumber;
  }
  billingAddress.addressType = 'ShippingAndBilling';

  return billingAddress;
}

export function* getCardDetails(formData, isPLCC) {
  const { paymentAction } = formData || {};
  let cardDetails;
  if (isPLCC) {
    cardDetails = {
      accountNo: formData.cardNumber,
    };
  } else {
    cardDetails = {
      accountNo: formData.cardNumber,
      expMonth: formData.expMonth,
      expYear: formData.expYear,
      cvv: formData.cvv,
    };
  }
  if (formData.onFileCardId && paymentAction !== CHECKOUT_CONSTANTS.PAYMENT_ACTION.ADD) {
    cardDetails.creditCardId = formData.onFileCardId;
    const cardList = yield select(getCardsList, formData.onFileCardId);
    const savedCard = getSelectedCard({
      creditCardList: cardList,
      onFileCardKey: formData.onFileCardId,
    });
    if (savedCard) {
      cardDetails = { ...cardDetails, c_savedPI: formData.onFileCardId };
    }
  }
  return cardDetails;
}

export function* tokenizeCardAndGenerateSessionId(details) {
  const { sessionId, cardDetails, billingAddress } = details;
  let newSessionId = sessionId;
  const tokenResponse = yield call(tokenizeCard, sessionId, cardDetails, billingAddress);
  if (tokenResponse.SESSION_ID_HAS_BEEN_EXPIRED) {
    const res = yield call(fetchPaySecureSessionIdSaga, true);
    newSessionId = res.SessionId;
    yield call(tokenizeCard, newSessionId, cardDetails, billingAddress);
  }
  return newSessionId;
}

export function* recursiveTokenization(numberOfReattempts = 0, details) {
  const remainingAttempts = numberOfReattempts - 1;
  try {
    return yield call(tokenizeCardAndGenerateSessionId, details);
  } catch (err) {
    // Retrying only when network call gets failed
    if (remainingAttempts >= 0 && err.networkStatusCode !== 200) {
      return yield call(recursiveTokenization, remainingAttempts, details);
    }
    if (err.networkStatusCode !== 200) {
      logger.error({
        error: err,
        extraData: {
          methodName: 'PaySecure - Card Tokenization ',
          sessionId: details.sessionId,
          billingAddress: details.billingAddress,
        },
      });
    }
    throw err;
  }
}

export function* paySecureOperations(isMaskedCard, cardDetails, billingAddress) {
  const disableTokenization = parseBoolean(getAPIConfig().disableTokenization);
  // Session id
  let sessionId = yield select(getPaySecureSessionId);
  if (!isMaskedCard && !sessionId) {
    const resp = yield call(fetchPaySecureSessionIdSaga);
    sessionId = resp.SessionId;
  }

  // Tokenize Card
  if (!disableTokenization) {
    const hasTokenized = yield select(hasCardBeenTokenized);
    if (!hasTokenized && !isMaskedCard) {
      sessionId = yield call(
        recursiveTokenization,
        CONSTANTS.NUMBER_OF_RETRY_FOR_CARD_TOKENIZATION_API,
        {
          sessionId,
          cardDetails,
          billingAddress,
        }
      );
      yield put(updateCardTokenization(true));
    }
  }

  return sessionId;
}

export function* checkForPaySecure(isMaskedCard, cardDetails, billingAddress) {
  let sessionId;
  try {
    sessionId = yield call(paySecureOperations, isMaskedCard, cardDetails, billingAddress);
  } catch (e) {
    // Returning session ID in catch block to call isOfflineCardPayment for all scenarios
    return sessionId;
  }

  return sessionId;
}

const checkReqFields = (billingAddress) => {
  const { addressLine1, city, country, zipCode, state } = billingAddress || {};
  if (country?.toUpperCase() !== 'US' && country?.toUpperCase() !== 'CA') {
    return addressLine1 && city && country;
  }
  return addressLine1 && city && country && zipCode && state;
};

const checkIfPlccHasInternationalBillingAddress = (billingAddress, isPLCC) => {
  const { country } = billingAddress || {};
  if (isPLCC && country?.toUpperCase() !== 'US' && country?.toUpperCase() !== 'CA') {
    return true;
  }
  return false;
};

export function* addPaymentInstrument(
  billingAddress,
  sessionId,
  cardDetails,
  isMaskedCard,
  isPLCC,
  isGuestUser,
  allErrorMapping,
  recaptchaToken,
  fromBilling,
  differentCardSelectedFromList
) {
  if (!checkReqFields(billingAddress)) {
    logger.error({
      error: 'Missing billing address in addPaymentInstrument',
      extraData: {
        method: 'CheckoutBilling.saga.util',
        methodName: 'addPaymentInstrument',
        isPLCC,
        isGuestUser,
        fromBilling,
        cardType: cardDetails?.cardType || '',
        isAfterPay: cardDetails?.afterpay || false,
      },
    });
    throw new Error('_ERR_BILLING_ADDRESS_MISSING');
  }
  if (checkIfPlccHasInternationalBillingAddress(billingAddress, isPLCC)) {
    logger.error({
      error: 'PLCC Card Cannot have International Billing Address',
      extraData: {
        method: 'CheckoutBilling.saga.util',
        methodName: 'addPaymentInstrument',
        isPLCC,
        isGuestUser,
        fromBilling,
        cardType: cardDetails?.cardType || '',
        isAfterPay: cardDetails?.afterpay || false,
      },
    });
    throw new Error('_ERR_INTERNATIONAL_BILLING_ADDRESS_PLCC');
  }
  let basketResponse;
  const isPaySecureFailed = yield select(getIsPaySecureFailed);
  const isOffline = isMaskedCard ? isPaySecureFailed && !differentCardSelectedFromList : !sessionId;
  const isOfflineCardPayment = !isPLCC && isOffline;

  if (isOfflineCardPayment && cardDetails && cardDetails?.accountNo) {
    yield call(addPIOfflineToSFCC, cardDetails, isMaskedCard);

    basketResponse = yield call(
      addPaymentInstruction,
      billingAddress,
      null,
      allErrorMapping,
      recaptchaToken
    );
  } else {
    const paymentInstruction = getPaymentInstruction(
      cardDetails,
      isMaskedCard,
      sessionId,
      isPLCC,
      isGuestUser,
      cardDetails.afterpay
    );

    basketResponse = yield call(
      addPaymentInstruction,
      billingAddress,
      paymentInstruction,
      allErrorMapping,
      recaptchaToken
    );
  }

  return basketResponse;
}
